import { Component, OnInit, Signal, WritableSignal, computed, inject, input, signal, viewChild } from '@angular/core';
import {
  ButtonComponent, DropdownComponent, FormFieldComponent, FormFieldLabelDirective, IconComponent,
  ModalActionsDirective,
  ModalComponent, ModalService,
  ModalTitleDirective, TooltipTargetDirective,
} from '@totalenergiescode/tds-angular';
import { RegionsViewModel, selectRegionsViewModel } from '@store/scenario/regions/regions-slice';
import { dispatch, select } from '@store/injectables';
import { SourcesViewModel, selectSourcesViewModel } from '@store/scenario/sources/sources-slice';
import {
  SubscriptionViewModel,
  selectSubscriptionViewModel,
  subscribeToNotification,
  unsubscribeToNotification,
} from '@store/scenario/subscription/subscription-slice';
import { Zone } from '@scenario/models/zone.model';
import { Source } from '@scenario/models/source.model';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from '@scenario/models/subscription.model';
import { PsfConfirmationModalComponent } from '@app/modal/confirmation/psf-confirmation-modal.component';

type SubscriptionDto = {id:number, zoneId: number | null, sourceId: number | null, description:string};

@Component({
  selector: 'app-psf-subscription-modal2-component',
  imports: [
    ButtonComponent,
    ModalActionsDirective,
    ModalComponent,
    ModalTitleDirective,
    IconComponent,
    FormFieldLabelDirective,
    FormFieldComponent,
    DropdownComponent,
    ReactiveFormsModule,
    TooltipTargetDirective
  ],
  templateUrl: './psf-subscription-modal.component.html'
})
export class PsfSubscriptionModalComponent implements OnInit {
  dispatch = dispatch();
  private readonly modalService = inject(ModalService);
  public readonly modal = viewChild.required(ModalComponent);

  defaultZone = input<string>();
  defaultSource = input<string>();

  regionsViewModel: Signal<RegionsViewModel> = select(selectRegionsViewModel);
  sourcesViewModel: Signal<SourcesViewModel> = select(selectSourcesViewModel);
  subscriptionSignal: Signal<SubscriptionViewModel> = select(selectSubscriptionViewModel);
  subscriptions = computed(()=> this.subscriptionSignal().subscriptions);
  regionsIdsAlreadySubscribed: WritableSignal<number[]> = signal([]);

  sourcesOptions = computed(() => {
    return this.sourcesViewModel().sources
      .map((source) => ({
        value: source.id,
        label: source.name,
      }));
  });

  regionsOptions = computed(() => {
    return this.regionsViewModel().regions
      .filter((region) => !this.regionsIdsAlreadySubscribed().includes(region.id))
      .map((region) => ({
        value: region.id,
        label: region.name + ' - ' + region.alias,
      }));
  });

  subscriptionForm = new FormGroup({
    zone: new FormControl(),
    source: new FormControl(),
  });

  ngOnInit() {
    if (this.defaultZone()) {
      const defaultZoneOption = this.regionsViewModel().regions.find(
        (region) => region.alias === this.defaultZone()
      );
      this.subscriptionForm.controls['zone'].patchValue(defaultZoneOption?.id);
    }
    if (this.defaultSource()) {
      const defaultSourceOption = this.sourcesViewModel().sources.find(
        (source) => source.name === this.defaultSource()
      );
      this.subscriptionForm.controls['source'].patchValue(defaultSourceOption?.id);
    }
    this.regionsIdsAlreadySubscribed.set(this.subscriptions().map((sub) => sub.zoneId).filter((id): id is number => id !== null));
  }

  close(action?: string) {
    this.modal().closeModal(action);
  }

  subscribeToNotification() {

    this.dispatch(
      subscribeToNotification({
        sourceId: this.subscriptionForm.value.source,
        zoneId: this.subscriptionForm.value.zone,
      }),
    );

    if(this.subscriptionForm.value.zone) {
      this.regionsIdsAlreadySubscribed.update((ids) => [...ids, this.subscriptionForm.value.zone]);
    }

    this.subscriptionForm.reset();
  }

  subscribeToNotificationAndCloseModal() {
    this.subscribeToNotification();
    this.modal().closeModal();
  }

  isSubscriptionAllowed() {
    return this.subscriptionForm.value.zone ?? this.subscriptionForm.value.source;
  }

  unsubscribeToNotification(subscription: SubscriptionDto) {
    this.modalService.open({
      content : {
        component: PsfConfirmationModalComponent,
        inputs: {
          title : 'Do you want to unsubscribe ?',
           text : `You won’t receive updates of '${subscription.description}' via emails anymore`,
           closeText: 'Keep subscription',
           confirmText: 'Unsubscribe',
         }
       },
       onClose: (action) => {
        if(action === 'actionConfirmed') {
          this.dispatch(unsubscribeToNotification(subscription.id));
          if(subscription.zoneId) {
            this.regionsIdsAlreadySubscribed.update((ids) => ids.filter((id) => id !== subscription.zoneId));
          }
        }
       },
    });
  }

  mapSubcriptionIdsWithHisRegionAndSource() {
    const subscriptions:SubscriptionDto[] = [];

    this.subscriptions().forEach((subscription) => {
      subscriptions.push({
        id: subscription.id,
        zoneId: subscription.zoneId,
        sourceId: subscription.sourceId,
        description: this.formatSubscriptionDescription(subscription),
      });
    });

    return subscriptions;
  }

  formatSubscriptionDescription(subscription: Subscription): string {
    const zone = this.regionsViewModel().regions.find((zone: Zone) => zone.id === subscription.zoneId) ?? null;
    const source = this.sourcesViewModel().sources.find((source: Source) => source.id === subscription.sourceId) ?? null;

    if (zone && source) {
      return `${zone.name} - ${source.name}`;
    } else if (zone) {
      return `${zone.name} - All Sources`;
    } else if (source) {
      return `All Zones - ${source.name}`;
    } else {
      throw new Error('Invalid subscription: both zone and source are null');
    }
  }

}
